import React, { useState, useEffect } from 'react';

/**
 * Smooth Area Sparkline Component
 * Shows a clean mini area chart for trend data
 */
const InlineTrendChart = ({ data, color = '#2563eb', currentValue }) => {
    if (!data || data.length === 0) {
        return <span className="text-gray-900 font-medium">{currentValue}</span>;
    }

    // Chart dimensions
    const width = 60;
    const height = 24;
    const padding = 2;

    // Find min and max values for scaling
    const values = data.map(d => d.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue || 1;

    // Generate points
    const points = data.map((item, index) => {
        const x = padding + (index * (width - 2 * padding)) / (data.length - 1);
        const y = height - padding - ((item.value - minValue) / range) * (height - 2 * padding);
        return { x, y, item, index };
    });

    // Generate paths
    let linePath = `M ${points[0].x} ${points[0].y}`;
    let areaPath = `M ${points[0].x} ${height - padding} L ${points[0].x} ${points[0].y}`;
    
    for (let i = 1; i < points.length; i++) {
        linePath += ` L ${points[i].x} ${points[i].y}`;
        areaPath += ` L ${points[i].x} ${points[i].y}`;
    }
    
    areaPath += ` L ${points[points.length - 1].x} ${height - padding} Z`;

    // Build title text with all data points
    const titleText = data.map(d => `${d.periodLabel}: ${d.value}% (${d.cohort})`).join('\n');

    return (
        <div 
            className="flex items-center justify-center"
            style={{ width: `${width}px`, height: `${height}px` }}
        >
            <svg 
                width={width} 
                height={height} 
                className="block cursor-help"
            >
                <title>{titleText}</title>
                
                {/* Area fill */}
                <path
                    d={areaPath}
                    fill={color}
                    fillOpacity={0.15}
                    stroke="none"
                />
                
                {/* Stroke line */}
                <path
                    d={linePath}
                    fill="none"
                    stroke={color}
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                
                {/* Data points */}
                {points.map((point, index) => (
                    <circle
                        key={index}
                        cx={point.x}
                        cy={point.y}
                        r={1.5}
                        fill="white"
                        stroke={color}
                        strokeWidth={1}
                    />
                ))}
            </svg>
        </div>
    );
};

/**
 * Generate mock trend data based on date range and current value
 */
const generateTrendData = (dateRange, currentValue, itemId) => {
    const periods = {
        '1m': 1,   // 1 month
        '3m': 3,   // 3 months  
        '6m': 6,   // 6 months
        '12m': 12  // 12 months
    };
    
    const numPoints = periods[dateRange] || 4;
    const data = [];
    
    // Parse current value (remove % and convert to number)
    const baseValue = parseFloat(currentValue.replace('%', '')) || 0;
    
    // Create some realistic variation based on item ID for consistency
    const seed = itemId ? itemId.split('').reduce((a, b) => a + b.charCodeAt(0), 0) : 0;
    
    // Define pricing band cohorts
    const getPricingCohort = (value) => {
        if (value >= 20) return '≥20% above market';
        if (value >= 10) return '10-20% above market';
        if (value >= 0) return '0-10% above market';
        if (value >= -10) return '0-10% below market';
        if (value >= -20) return '10-20% below market';
        return '≥20% below market';
    };
    
    // Calculate start date based on current date and range
    const endDate = new Date();
    const startDate = new Date();
    
    switch (dateRange) {
        case '3m':
            startDate.setMonth(endDate.getMonth() - 3);
            break;
        case '6m':
            startDate.setMonth(endDate.getMonth() - 6);
            break;
        case '12m':
            startDate.setMonth(endDate.getMonth() - 12);
            break;
        default:
            startDate.setMonth(endDate.getMonth() - 1);
    }
    
    for (let i = 0; i < numPoints; i++) {
        // Create some realistic trend variation
        const variation = Math.sin((seed + i) * 0.5) * 5 + Math.random() * 3 - 1.5;
        const value = Math.max(0, baseValue + variation - (i * 0.2)); // Slight downward trend
        
        // Calculate the date for this period (monthly intervals)
        const periodDate = new Date(startDate);
        periodDate.setMonth(startDate.getMonth() + i);
        
        // Format period label
        const periodLabel = periodDate.toLocaleDateString('en-US', { 
            month: 'short', 
            year: 'numeric' 
        });
        
        const roundedValue = Math.round(value * 10) / 10;
        
        data.push({
            period: i,
            value: roundedValue,
            periodLabel: periodLabel,
            cohort: getPricingCohort(roundedValue),
            date: periodDate.toISOString().split('T')[0]
        });
    }
    
    return data;
};

/**
 * PricingIntelligenceTable Component
 * Displays pricing intelligence data in a professional table format
 */
const PricingIntelligenceTable = ({ filters = {} }) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(25);
    const [searchInput, setSearchInput] = useState(''); // What user types
    const [searchTerm, setSearchTerm] = useState(''); // What gets sent to API
    const [pricingData, setPricingData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    // Load pricing intelligence data from API
    const loadPricingData = async () => {
        try {
            setIsLoading(true);
            const authToken = localStorage.getItem('authToken');
            
            // Build query parameters from filters and pagination
            const params = new URLSearchParams();
            
            // Add filter parameters
            if (filters.vendor && filters.vendor !== 'all') {
                params.append('vendor_id', filters.vendor);
            }
            if (filters.gdc && filters.gdc !== 'all') {
                params.append('gdc', filters.gdc);
            }
            if (filters.cohort && filters.cohort !== 'all') {
                params.append('cohort_id', filters.cohort);
            }
            if (filters.month && filters.month !== 'current') {
                params.append('month_key', filters.month);
            }
            
            // Handle date range filters
            if (filters.dateRange && filters.dateRange !== '1m') {
                // For demo purposes, use May 2025 as the base date since that's where the data is
                const baseDate = new Date('2025-05-31'); // End of May 2025
                let dateFrom;
                switch (filters.dateRange) {
                    case '3m':
                        dateFrom = new Date(2025, 2, 1); // March 2025
                        break;
                    case '6m':
                        dateFrom = new Date(2024, 11, 1); // December 2024
                        break;
                    case '12m':
                        dateFrom = new Date(2024, 5, 1); // June 2024
                        break;
                    default:
                        dateFrom = new Date(2025, 4, 1); // May 2025
                }
                params.append('date_from', dateFrom.toISOString().split('T')[0]);
                params.append('date_to', baseDate.toISOString().split('T')[0]);
            } else {
                // Default to May 2025 where the sample data exists
                params.append('date_from', '2025-05-01');
                params.append('date_to', '2025-05-31');
            }
            
            // Add search parameter
            if (searchTerm) {
                params.append('search', searchTerm);
            }
            
            // Add pagination parameters
            params.append('limit', itemsPerPage);
            params.append('offset', (currentPage - 1) * itemsPerPage);
            
            const url = `/api/snowflake/pricing-intelligence${params.toString() ? '?' + params.toString() : ''}`;
            console.log('📊 Loading pricing intelligence data from:', url);
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            const data = await response.json();
            
            if (data.success) {
                // Log first item to debug structure
                if (data.data && data.data.length > 0) {
                    console.log('First pricing item structure:', data.data[0]);
                }
                
                // Transform API data to match component expectations
                const transformedData = data.data.map((item, index) => ({
                    id: index + 1 + (currentPage - 1) * itemsPerPage,
                    vendor: item.vendorName,
                    gdc: item.gdcCode,
                    gdcDescription: item.gdcDescription,
                    gdcRank: item.gdcRank,
                    itemId: item.itemId,
                    itemDescription: item.itemDescription,
                    vendorsInAnchor: item.vendors,
                    bandVsMarket: item.bandVsMarket !== undefined && item.bandVsMarket !== null ? 
                        item.bandVsMarket : 'N/A',
                    bandVsLeader: item.bandVsLeader !== undefined && item.bandVsLeader !== null ? 
                        item.bandVsLeader : 'N/A',
                    trend: Math.random() > 0.5 ? 'up' : 'down' // Sample trend data
                }));
                
                setPricingData(transformedData);
                setTotalCount(data.totalCount || transformedData.length);
                console.log(`✅ Loaded ${transformedData.length} pricing intelligence records`);
            } else {
                console.error('Failed to load pricing intelligence data:', data.message);
                setPricingData([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Failed to load pricing intelligence data:', error);
            setPricingData([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle search when user presses Enter
    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1); // Reset to first page when searching
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Load data when component mounts or dependencies change
    useEffect(() => {
        loadPricingData();
    }, [filters, currentPage, searchTerm]);

    // Sample fallback data (will be replaced by API data)
    const sampleData = [
        {
            id: 1,
            vendor: 'BuRx Pharma',
            gdc: '70934',
            gdcDescription: 'LAMICTAL 25 MG TABLET',
            gdcRank: '10',
            itemId: 'BRX00001',
            itemDescription: 'Lamotrigine 25 mg tablets bottle 30',
            vendorsInAnchor: '14',
            bandVsMarket: '6.5% of market',
            bandVsLeader: '+1.2% above market',
            trend: 'up'
        },
        {
            id: 2,
            vendor: 'BuRx Pharma',
            gdc: '70934',
            gdcDescription: 'LAMICTAL 25 MG TABLET',
            gdcRank: '10',
            itemId: 'BRX00002',
            itemDescription: 'Lamotrigine 25 mg tablets bottle 90',
            vendorsInAnchor: '14',
            bandVsMarket: '6.5% of market',
            bandVsLeader: '+1.2% above market',
            trend: 'up'
        },
        {
            id: 3,
            vendor: 'BuRx Pharma',
            gdc: '30842',
            gdcDescription: 'LOVENOX 40 MG PREFILLED SYR',
            gdcRank: '25',
            itemId: 'BRX00003',
            itemDescription: 'Enoxaparin 40 mg prefilled syringe 5',
            vendorsInAnchor: '7',
            bandVsMarket: '4.2% of market',
            bandVsLeader: '-0.8% of market',
            trend: 'down'
        },
        {
            id: 4,
            vendor: 'BuRx Pharma',
            gdc: '30842',
            gdcDescription: 'SPIRIVA 18 MCG HANDIHALER',
            gdcRank: '24',
            itemId: 'BRX00004',
            itemDescription: 'Tiotropium 18 mcg inhaler capsules 30',
            vendorsInAnchor: '30',
            bandVsMarket: '8.1% of market',
            bandVsLeader: '+2.3% above market',
            trend: 'up'
        },
        {
            id: 5,
            vendor: 'BuRx Pharma',
            gdc: '68382',
            gdcDescription: 'WARFARIN 5MG 25 MG TABLET',
            gdcRank: '30',
            itemId: 'BRX00005',
            itemDescription: 'Warfarin 5mg tablets bottle 100',
            vendorsInAnchor: '7',
            bandVsMarket: '12.3% of market',
            bandVsLeader: '0.1% above market',
            trend: 'stable'
        },
        {
            id: 6,
            vendor: 'BuRx Pharma',
            gdc: '41963',
            gdcDescription: 'CRESTOR 20MG TABLET',
            gdcRank: '30',
            itemId: 'BRX00006',
            itemDescription: 'Rosuvastatin 20mg tablets bottle 30',
            vendorsInAnchor: '14',
            bandVsMarket: '9.7% of market',
            bandVsLeader: '-1.5% above market',
            trend: 'down'
        },
        {
            id: 7,
            vendor: 'BuRx Pharma',
            gdc: '41963',
            gdcDescription: 'CRESTOR 20MG TABLET',
            gdcRank: '30',
            itemId: 'BRX00007',
            itemDescription: 'Rosuvastatin 20mg tablets bottle 90',
            vendorsInAnchor: '14',
            bandVsMarket: '9.7% of market',
            bandVsLeader: '-1.5% above market',
            trend: 'down'
        },
        {
            id: 8,
            vendor: 'BuRx Pharma',
            gdc: '41963',
            gdcDescription: 'CRESTOR 20MG TABLET',
            gdcRank: '17',
            itemId: 'BRX00008',
            itemDescription: 'Rosuvastatin 20mg tablets bottle 60',
            vendorsInAnchor: '17',
            bandVsMarket: '9.7% of market',
            bandVsLeader: '+1.7% above market',
            trend: 'up'
        },
        {
            id: 9,
            vendor: 'BuRx Pharma',
            gdc: '24658',
            gdcDescription: 'PROAIR HFA 90 MCG INHALER',
            gdcRank: '24',
            itemId: 'BRX00009',
            itemDescription: 'Albuterol 90 mcg inhaler 8.5g',
            vendorsInAnchor: '14',
            bandVsMarket: '15.2% of market',
            bandVsLeader: '0.3% of market',
            trend: 'stable'
        },
        {
            id: 10,
            vendor: 'MedCore Solutions',
            gdc: '52468',
            gdcDescription: 'NEXIUM 40 MG CAPSULE',
            gdcRank: '15',
            itemId: 'MCS00010',
            itemDescription: 'Esomeprazole 40 mg capsules bottle 30',
            vendorsInAnchor: '22',
            bandVsMarket: '11.8% of market',
            bandVsLeader: '+3.1% above market',
            trend: 'up'
        },
        {
            id: 11,
            vendor: 'MedCore Solutions',
            gdc: '73951',
            gdcDescription: 'ADVAIR DISKUS 250/50',
            gdcRank: '8',
            itemId: 'MCS00011',
            itemDescription: 'Fluticasone/Salmeterol 250/50 inhaler',
            vendorsInAnchor: '18',
            bandVsMarket: '7.3% of market',
            bandVsLeader: '-2.1% below market',
            trend: 'down'
        },
        {
            id: 12,
            vendor: 'MedCore Solutions',
            gdc: '84627',
            gdcDescription: 'JANUVIA 100 MG TABLET',
            gdcRank: '12',
            itemId: 'MCS00012',
            itemDescription: 'Sitagliptin 100 mg tablets bottle 30',
            vendorsInAnchor: '26',
            bandVsMarket: '13.5% of market',
            bandVsLeader: '+0.7% above market',
            trend: 'stable'
        },
        {
            id: 13,
            vendor: 'PharmaTech Inc',
            gdc: '95173',
            gdcDescription: 'LYRICA 150 MG CAPSULE',
            gdcRank: '20',
            itemId: 'PTI00013',
            itemDescription: 'Pregabalin 150 mg capsules bottle 60',
            vendorsInAnchor: '15',
            bandVsMarket: '5.9% of market',
            bandVsLeader: '+4.2% above market',
            trend: 'up'
        },
        {
            id: 14,
            vendor: 'PharmaTech Inc',
            gdc: '62840',
            gdcDescription: 'SYMBICORT 160/4.5',
            gdcRank: '18',
            itemId: 'PTI00014',
            itemDescription: 'Budesonide/Formoterol 160/4.5 inhaler',
            vendorsInAnchor: '21',
            bandVsMarket: '8.7% of market',
            bandVsLeader: '-1.3% below market',
            trend: 'down'
        },
        {
            id: 15,
            vendor: 'PharmaTech Inc',
            gdc: '37295',
            gdcDescription: 'HUMIRA 40 MG SYRINGE',
            gdcRank: '5',
            itemId: 'PTI00015',
            itemDescription: 'Adalimumab 40 mg prefilled syringe',
            vendorsInAnchor: '9',
            bandVsMarket: '2.1% of market',
            bandVsLeader: '+8.9% above market',
            trend: 'up'
        },
        {
            id: 16,
            vendor: 'HealthFirst Rx',
            gdc: '48562',
            gdcDescription: 'ABILIFY 10 MG TABLET',
            gdcRank: '22',
            itemId: 'HFR00016',
            itemDescription: 'Aripiprazole 10 mg tablets bottle 30',
            vendorsInAnchor: '19',
            bandVsMarket: '6.4% of market',
            bandVsLeader: '+2.8% above market',
            trend: 'up'
        },
        {
            id: 17,
            vendor: 'HealthFirst Rx',
            gdc: '59384',
            gdcDescription: 'SEROQUEL XR 300 MG',
            gdcRank: '28',
            itemId: 'HFR00017',
            itemDescription: 'Quetiapine XR 300 mg tablets bottle 60',
            vendorsInAnchor: '13',
            bandVsMarket: '4.8% of market',
            bandVsLeader: '-0.9% below market',
            trend: 'down'
        },
        {
            id: 18,
            vendor: 'HealthFirst Rx',
            gdc: '71629',
            gdcDescription: 'LANTUS SOLOSTAR PEN',
            gdcRank: '6',
            itemId: 'HFR00018',
            itemDescription: 'Insulin glargine 100 units/ml pen 3ml',
            vendorsInAnchor: '11',
            bandVsMarket: '3.2% of market',
            bandVsLeader: '+5.6% above market',
            trend: 'up'
        },
        {
            id: 19,
            vendor: 'Global Meds Co',
            gdc: '83746',
            gdcDescription: 'CYMBALTA 60 MG CAPSULE',
            gdcRank: '16',
            itemId: 'GMC00019',
            itemDescription: 'Duloxetine 60 mg capsules bottle 30',
            vendorsInAnchor: '24',
            bandVsMarket: '10.1% of market',
            bandVsLeader: '+1.4% above market',
            trend: 'stable'
        },
        {
            id: 20,
            vendor: 'Global Meds Co',
            gdc: '92517',
            gdcDescription: 'CELEBREX 200 MG CAPSULE',
            gdcRank: '25',
            itemId: 'GMC00020',
            itemDescription: 'Celecoxib 200 mg capsules bottle 60',
            vendorsInAnchor: '17',
            bandVsMarket: '7.6% of market',
            bandVsLeader: '-2.3% below market',
            trend: 'down'
        },
        {
            id: 21,
            vendor: 'Global Meds Co',
            gdc: '64831',
            gdcDescription: 'VIAGRA 100 MG TABLET',
            gdcRank: '11',
            itemId: 'GMC00021',
            itemDescription: 'Sildenafil 100 mg tablets bottle 30',
            vendorsInAnchor: '20',
            bandVsMarket: '9.3% of market',
            bandVsLeader: '+3.7% above market',
            trend: 'up'
        },
        {
            id: 22,
            vendor: 'Apex Pharmaceuticals',
            gdc: '75942',
            gdcDescription: 'FLOMAX 0.4 MG CAPSULE',
            gdcRank: '19',
            itemId: 'APX00022',
            itemDescription: 'Tamsulosin 0.4 mg capsules bottle 30',
            vendorsInAnchor: '16',
            bandVsMarket: '5.7% of market',
            bandVsLeader: '+2.1% above market',
            trend: 'stable'
        },
        {
            id: 23,
            vendor: 'Apex Pharmaceuticals',
            gdc: '86153',
            gdcDescription: 'SYNTHROID 100 MCG TABLET',
            gdcRank: '7',
            itemId: 'APX00023',
            itemDescription: 'Levothyroxine 100 mcg tablets bottle 90',
            vendorsInAnchor: '28',
            bandVsMarket: '14.2% of market',
            bandVsLeader: '+0.8% above market',
            trend: 'up'
        },
        {
            id: 24,
            vendor: 'Apex Pharmaceuticals',
            gdc: '97468',
            gdcDescription: 'PLAVIX 75 MG TABLET',
            gdcRank: '13',
            itemId: 'APX00024',
            itemDescription: 'Clopidogrel 75 mg tablets bottle 30',
            vendorsInAnchor: '23',
            bandVsMarket: '8.9% of market',
            bandVsLeader: '-1.7% below market',
            trend: 'down'
        },
        {
            id: 25,
            vendor: 'Premier Drug Supply',
            gdc: '58274',
            gdcDescription: 'DIOVAN 160 MG TABLET',
            gdcRank: '21',
            itemId: 'PDS00025',
            itemDescription: 'Valsartan 160 mg tablets bottle 30',
            vendorsInAnchor: '12',
            bandVsMarket: '6.1% of market',
            bandVsLeader: '+4.5% above market',
            trend: 'up'
        },
        {
            id: 26,
            vendor: 'Premier Drug Supply',
            gdc: '69385',
            gdcDescription: 'ACTOS 30 MG TABLET',
            gdcRank: '26',
            itemId: 'PDS00026',
            itemDescription: 'Pioglitazone 30 mg tablets bottle 30',
            vendorsInAnchor: '14',
            bandVsMarket: '4.3% of market',
            bandVsLeader: '-3.2% below market',
            trend: 'down'
        },
        {
            id: 27,
            vendor: 'Premier Drug Supply',
            gdc: '74196',
            gdcDescription: 'TRICOR 145 MG TABLET',
            gdcRank: '23',
            itemId: 'PDS00027',
            itemDescription: 'Fenofibrate 145 mg tablets bottle 90',
            vendorsInAnchor: '18',
            bandVsMarket: '7.8% of market',
            bandVsLeader: '+1.9% above market',
            trend: 'stable'
        },
        {
            id: 28,
            vendor: 'Elite Pharmacy Corp',
            gdc: '85207',
            gdcDescription: 'ARICEPT 10 MG TABLET',
            gdcRank: '27',
            itemId: 'EPC00028',
            itemDescription: 'Donepezil 10 mg tablets bottle 30',
            vendorsInAnchor: '10',
            bandVsMarket: '3.7% of market',
            bandVsLeader: '+6.3% above market',
            trend: 'up'
        },
        {
            id: 29,
            vendor: 'Elite Pharmacy Corp',
            gdc: '96518',
            gdcDescription: 'NAMENDA 10 MG TABLET',
            gdcRank: '29',
            itemId: 'EPC00029',
            itemDescription: 'Memantine 10 mg tablets bottle 60',
            vendorsInAnchor: '8',
            bandVsMarket: '2.9% of market',
            bandVsLeader: '+7.1% above market',
            trend: 'up'
        },
        {
            id: 30,
            vendor: 'Elite Pharmacy Corp',
            gdc: '47362',
            gdcDescription: 'BONIVA 150 MG TABLET',
            gdcRank: '30',
            itemId: 'EPC00030',
            itemDescription: 'Ibandronate 150 mg tablets bottle 3',
            vendorsInAnchor: '6',
            bandVsMarket: '1.8% of market',
            bandVsLeader: '+9.2% above market',
            trend: 'stable'
        },
        {
            id: 31,
            vendor: 'Meridian Pharmaceuticals',
            gdc: '58739',
            gdcDescription: 'METFORMIN 500 MG TABLET',
            gdcRank: '4',
            itemId: 'MER00031',
            itemDescription: 'Metformin 500 mg tablets bottle 100',
            vendorsInAnchor: '32',
            bandVsMarket: '18.5% of market',
            bandVsLeader: '+0.4% above market',
            trend: 'up'
        },
        {
            id: 32,
            vendor: 'Meridian Pharmaceuticals',
            gdc: '69482',
            gdcDescription: 'LISINOPRIL 10 MG TABLET',
            gdcRank: '9',
            itemId: 'MER00032',
            itemDescription: 'Lisinopril 10 mg tablets bottle 30',
            vendorsInAnchor: '27',
            bandVsMarket: '14.7% of market',
            bandVsLeader: '+1.8% above market',
            trend: 'stable'
        },
        {
            id: 33,
            vendor: 'Meridian Pharmaceuticals',
            gdc: '74185',
            gdcDescription: 'AMLODIPINE 5 MG TABLET',
            gdcRank: '14',
            itemId: 'MER00033',
            itemDescription: 'Amlodipine 5 mg tablets bottle 90',
            vendorsInAnchor: '25',
            bandVsMarket: '12.1% of market',
            bandVsLeader: '-0.6% below market',
            trend: 'down'
        },
        {
            id: 34,
            vendor: 'Cardinal Health Supply',
            gdc: '85296',
            gdcDescription: 'OMEPRAZOLE 20 MG CAPSULE',
            gdcRank: '16',
            itemId: 'CHS00034',
            itemDescription: 'Omeprazole 20 mg capsules bottle 30',
            vendorsInAnchor: '29',
            bandVsMarket: '13.8% of market',
            bandVsLeader: '+2.2% above market',
            trend: 'up'
        },
        {
            id: 35,
            vendor: 'Cardinal Health Supply',
            gdc: '96307',
            gdcDescription: 'ATORVASTATIN 20 MG TABLET',
            gdcRank: '11',
            itemId: 'CHS00035',
            itemDescription: 'Atorvastatin 20 mg tablets bottle 30',
            vendorsInAnchor: '31',
            bandVsMarket: '16.2% of market',
            bandVsLeader: '+1.1% above market',
            trend: 'stable'
        },
        {
            id: 36,
            vendor: 'Cardinal Health Supply',
            gdc: '47518',
            gdcDescription: 'HYDROCHLOROTHIAZIDE 25 MG',
            gdcRank: '22',
            itemId: 'CHS00036',
            itemDescription: 'Hydrochlorothiazide 25 mg tablets bottle 100',
            vendorsInAnchor: '19',
            bandVsMarket: '8.3% of market',
            bandVsLeader: '-1.4% below market',
            trend: 'down'
        },
        {
            id: 37,
            vendor: 'Northstar Medical',
            gdc: '58629',
            gdcDescription: 'GABAPENTIN 300 MG CAPSULE',
            gdcRank: '17',
            itemId: 'NSM00037',
            itemDescription: 'Gabapentin 300 mg capsules bottle 100',
            vendorsInAnchor: '22',
            bandVsMarket: '9.6% of market',
            bandVsLeader: '+3.4% above market',
            trend: 'up'
        },
        {
            id: 38,
            vendor: 'Northstar Medical',
            gdc: '69740',
            gdcDescription: 'PREDNISONE 20 MG TABLET',
            gdcRank: '26',
            itemId: 'NSM00038',
            itemDescription: 'Prednisone 20 mg tablets bottle 21',
            vendorsInAnchor: '16',
            bandVsMarket: '6.8% of market',
            bandVsLeader: '+2.7% above market',
            trend: 'stable'
        },
        {
            id: 39,
            vendor: 'Northstar Medical',
            gdc: '74851',
            gdcDescription: 'TRAMADOL 50 MG TABLET',
            gdcRank: '19',
            itemId: 'NSM00039',
            itemDescription: 'Tramadol 50 mg tablets bottle 30',
            vendorsInAnchor: '18',
            bandVsMarket: '7.4% of market',
            bandVsLeader: '+1.9% above market',
            trend: 'up'
        },
        {
            id: 40,
            vendor: 'Sunrise Pharmaceuticals',
            gdc: '85962',
            gdcDescription: 'SERTRALINE 50 MG TABLET',
            gdcRank: '21',
            itemId: 'SUN00040',
            itemDescription: 'Sertraline 50 mg tablets bottle 30',
            vendorsInAnchor: '24',
            bandVsMarket: '11.3% of market',
            bandVsLeader: '+0.8% above market',
            trend: 'stable'
        },
        {
            id: 41,
            vendor: 'Sunrise Pharmaceuticals',
            gdc: '96073',
            gdcDescription: 'TRAZODONE 50 MG TABLET',
            gdcRank: '24',
            itemId: 'SUN00041',
            itemDescription: 'Trazodone 50 mg tablets bottle 30',
            vendorsInAnchor: '15',
            bandVsMarket: '5.2% of market',
            bandVsLeader: '+4.1% above market',
            trend: 'up'
        },
        {
            id: 42,
            vendor: 'Sunrise Pharmaceuticals',
            gdc: '47184',
            gdcDescription: 'CYCLOBENZAPRINE 10 MG',
            gdcRank: '27',
            itemId: 'SUN00042',
            itemDescription: 'Cyclobenzaprine 10 mg tablets bottle 30',
            vendorsInAnchor: '13',
            bandVsMarket: '4.7% of market',
            bandVsLeader: '+3.8% above market',
            trend: 'up'
        },
        {
            id: 43,
            vendor: 'Vertex Pharmacy Solutions',
            gdc: '58295',
            gdcDescription: 'FUROSEMIDE 40 MG TABLET',
            gdcRank: '18',
            itemId: 'VPS00043',
            itemDescription: 'Furosemide 40 mg tablets bottle 100',
            vendorsInAnchor: '21',
            bandVsMarket: '8.9% of market',
            bandVsLeader: '+2.3% above market',
            trend: 'stable'
        },
        {
            id: 44,
            vendor: 'Vertex Pharmacy Solutions',
            gdc: '69406',
            gdcDescription: 'POTASSIUM CHLORIDE 20 MEQ',
            gdcRank: '29',
            itemId: 'VPS00044',
            itemDescription: 'Potassium chloride 20 mEq tablets bottle 100',
            vendorsInAnchor: '12',
            bandVsMarket: '3.4% of market',
            bandVsLeader: '+6.7% above market',
            trend: 'up'
        },
        {
            id: 45,
            vendor: 'Vertex Pharmacy Solutions',
            gdc: '74517',
            gdcDescription: 'PANTOPRAZOLE 40 MG TABLET',
            gdcRank: '15',
            itemId: 'VPS00045',
            itemDescription: 'Pantoprazole 40 mg tablets bottle 90',
            vendorsInAnchor: '26',
            bandVsMarket: '12.6% of market',
            bandVsLeader: '+1.5% above market',
            trend: 'up'
        },
        {
            id: 46,
            vendor: 'Oceanic Drug Corp',
            gdc: '85628',
            gdcDescription: 'LOSARTAN 50 MG TABLET',
            gdcRank: '13',
            itemId: 'ODC00046',
            itemDescription: 'Losartan 50 mg tablets bottle 30',
            vendorsInAnchor: '28',
            bandVsMarket: '15.4% of market',
            bandVsLeader: '+0.9% above market',
            trend: 'stable'
        },
        {
            id: 47,
            vendor: 'Oceanic Drug Corp',
            gdc: '96739',
            gdcDescription: 'MELOXICAM 15 MG TABLET',
            gdcRank: '23',
            itemId: 'ODC00047',
            itemDescription: 'Meloxicam 15 mg tablets bottle 30',
            vendorsInAnchor: '17',
            bandVsMarket: '6.1% of market',
            bandVsLeader: '+3.2% above market',
            trend: 'up'
        },
        {
            id: 48,
            vendor: 'Oceanic Drug Corp',
            gdc: '47840',
            gdcDescription: 'CLONAZEPAM 1 MG TABLET',
            gdcRank: '28',
            itemId: 'ODC00048',
            itemDescription: 'Clonazepam 1 mg tablets bottle 60',
            vendorsInAnchor: '14',
            bandVsMarket: '4.9% of market',
            bandVsLeader: '+4.6% above market',
            trend: 'up'
        },
        {
            id: 49,
            vendor: 'Pinnacle Health Systems',
            gdc: '58951',
            gdcDescription: 'ALPRAZOLAM 0.5 MG TABLET',
            gdcRank: '25',
            itemId: 'PHS00049',
            itemDescription: 'Alprazolam 0.5 mg tablets bottle 90',
            vendorsInAnchor: '19',
            bandVsMarket: '7.7% of market',
            bandVsLeader: '+2.8% above market',
            trend: 'stable'
        },
        {
            id: 50,
            vendor: 'Pinnacle Health Systems',
            gdc: '69062',
            gdcDescription: 'ESCITALOPRAM 10 MG TABLET',
            gdcRank: '20',
            itemId: 'PHS00050',
            itemDescription: 'Escitalopram 10 mg tablets bottle 30',
            vendorsInAnchor: '23',
            bandVsMarket: '10.8% of market',
            bandVsLeader: '+1.6% above market',
            trend: 'up'
        },
        {
            id: 51,
            vendor: 'Pinnacle Health Systems',
            gdc: '74173',
            gdcDescription: 'BUPROPION 150 MG TABLET',
            gdcRank: '22',
            itemId: 'PHS00051',
            itemDescription: 'Bupropion 150 mg tablets bottle 60',
            vendorsInAnchor: '20',
            bandVsMarket: '8.5% of market',
            bandVsLeader: '+2.1% above market',
            trend: 'stable'
        },
        {
            id: 52,
            vendor: 'Zenith Medical Supply',
            gdc: '85284',
            gdcDescription: 'MONTELUKAST 10 MG TABLET',
            gdcRank: '17',
            itemId: 'ZMS00052',
            itemDescription: 'Montelukast 10 mg tablets bottle 30',
            vendorsInAnchor: '25',
            bandVsMarket: '11.9% of market',
            bandVsLeader: '+1.3% above market',
            trend: 'up'
        },
        {
            id: 53,
            vendor: 'Zenith Medical Supply',
            gdc: '96395',
            gdcDescription: 'CETIRIZINE 10 MG TABLET',
            gdcRank: '26',
            itemId: 'ZMS00053',
            itemDescription: 'Cetirizine 10 mg tablets bottle 30',
            vendorsInAnchor: '16',
            bandVsMarket: '6.3% of market',
            bandVsLeader: '+3.5% above market',
            trend: 'up'
        },
        {
            id: 54,
            vendor: 'Zenith Medical Supply',
            gdc: '47506',
            gdcDescription: 'RANITIDINE 150 MG TABLET',
            gdcRank: '29',
            itemId: 'ZMS00054',
            itemDescription: 'Ranitidine 150 mg tablets bottle 60',
            vendorsInAnchor: '11',
            bandVsMarket: '3.1% of market',
            bandVsLeader: '+7.4% above market',
            trend: 'stable'
        },
        {
            id: 55,
            vendor: 'Atlas Pharmaceutical Group',
            gdc: '58617',
            gdcDescription: 'DOXYCYCLINE 100 MG CAPSULE',
            gdcRank: '21',
            itemId: 'APG00055',
            itemDescription: 'Doxycycline 100 mg capsules bottle 20',
            vendorsInAnchor: '18',
            bandVsMarket: '7.8% of market',
            bandVsLeader: '+2.6% above market',
            trend: 'up'
        },
        {
            id: 56,
            vendor: 'Atlas Pharmaceutical Group',
            gdc: '69728',
            gdcDescription: 'AZITHROMYCIN 250 MG TABLET',
            gdcRank: '19',
            itemId: 'APG00056',
            itemDescription: 'Azithromycin 250 mg tablets bottle 6',
            vendorsInAnchor: '22',
            bandVsMarket: '9.2% of market',
            bandVsLeader: '+2.4% above market',
            trend: 'stable'
        },
        {
            id: 57,
            vendor: 'Atlas Pharmaceutical Group',
            gdc: '74839',
            gdcDescription: 'AMOXICILLIN 500 MG CAPSULE',
            gdcRank: '12',
            itemId: 'APG00057',
            itemDescription: 'Amoxicillin 500 mg capsules bottle 30',
            vendorsInAnchor: '30',
            bandVsMarket: '16.7% of market',
            bandVsLeader: '+0.7% above market',
            trend: 'up'
        },
        {
            id: 58,
            vendor: 'Quantum Pharma Solutions',
            gdc: '85940',
            gdcDescription: 'CIPROFLOXACIN 500 MG TABLET',
            gdcRank: '24',
            itemId: 'QPS00058',
            itemDescription: 'Ciprofloxacin 500 mg tablets bottle 20',
            vendorsInAnchor: '17',
            bandVsMarket: '6.5% of market',
            bandVsLeader: '+3.7% above market',
            trend: 'up'
        },
        {
            id: 59,
            vendor: 'Quantum Pharma Solutions',
            gdc: '96051',
            gdcDescription: 'CEPHALEXIN 500 MG CAPSULE',
            gdcRank: '18',
            itemId: 'QPS00059',
            itemDescription: 'Cephalexin 500 mg capsules bottle 28',
            vendorsInAnchor: '24',
            bandVsMarket: '10.4% of market',
            bandVsLeader: '+1.8% above market',
            trend: 'stable'
        },
        {
            id: 60,
            vendor: 'Quantum Pharma Solutions',
            gdc: '47162',
            gdcDescription: 'CLINDAMYCIN 300 MG CAPSULE',
            gdcRank: '27',
            itemId: 'QPS00060',
            itemDescription: 'Clindamycin 300 mg capsules bottle 16',
            vendorsInAnchor: '13',
            bandVsMarket: '4.2% of market',
            bandVsLeader: '+5.1% above market',
            trend: 'up'
        },
        {
            id: 61,
            vendor: 'Horizon Medical Corp',
            gdc: '58273',
            gdcDescription: 'METHYLPREDNISOLONE 4 MG',
            gdcRank: '23',
            itemId: 'HMC00061',
            itemDescription: 'Methylprednisolone 4 mg tablets bottle 21',
            vendorsInAnchor: '15',
            bandVsMarket: '5.8% of market',
            bandVsLeader: '+4.3% above market',
            trend: 'up'
        },
        {
            id: 62,
            vendor: 'Horizon Medical Corp',
            gdc: '69384',
            gdcDescription: 'DICLOFENAC 75 MG TABLET',
            gdcRank: '25',
            itemId: 'HMC00062',
            itemDescription: 'Diclofenac 75 mg tablets bottle 60',
            vendorsInAnchor: '16',
            bandVsMarket: '6.7% of market',
            bandVsLeader: '+3.1% above market',
            trend: 'stable'
        },
        {
            id: 63,
            vendor: 'Horizon Medical Corp',
            gdc: '74495',
            gdcDescription: 'NAPROXEN 500 MG TABLET',
            gdcRank: '20',
            itemId: 'HMC00063',
            itemDescription: 'Naproxen 500 mg tablets bottle 60',
            vendorsInAnchor: '21',
            bandVsMarket: '8.6% of market',
            bandVsLeader: '+2.2% above market',
            trend: 'up'
        },
        {
            id: 64,
            vendor: 'Stellar Pharmaceuticals',
            gdc: '85606',
            gdcDescription: 'IBUPROFEN 600 MG TABLET',
            gdcRank: '16',
            itemId: 'SPH00064',
            itemDescription: 'Ibuprofen 600 mg tablets bottle 100',
            vendorsInAnchor: '27',
            bandVsMarket: '13.2% of market',
            bandVsLeader: '+1.4% above market',
            trend: 'stable'
        },
        {
            id: 65,
            vendor: 'Stellar Pharmaceuticals',
            gdc: '96717',
            gdcDescription: 'ACETAMINOPHEN 500 MG TABLET',
            gdcRank: '8',
            itemId: 'SPH00065',
            itemDescription: 'Acetaminophen 500 mg tablets bottle 100',
            vendorsInAnchor: '35',
            bandVsMarket: '19.8% of market',
            bandVsLeader: '+0.3% above market',
            trend: 'up'
        },
        {
            id: 66,
            vendor: 'Stellar Pharmaceuticals',
            gdc: '47828',
            gdcDescription: 'ASPIRIN 81 MG TABLET',
            gdcRank: '6',
            itemId: 'SPH00066',
            itemDescription: 'Aspirin 81 mg tablets bottle 120',
            vendorsInAnchor: '33',
            bandVsMarket: '18.1% of market',
            bandVsLeader: '+0.5% above market',
            trend: 'stable'
        },
        {
            id: 67,
            vendor: 'Nexus Health Solutions',
            gdc: '58939',
            gdcDescription: 'MULTIVITAMIN TABLET',
            gdcRank: '30',
            itemId: 'NHS00067',
            itemDescription: 'Multivitamin tablets bottle 100',
            vendorsInAnchor: '8',
            bandVsMarket: '2.4% of market',
            bandVsLeader: '+8.7% above market',
            trend: 'up'
        },
        {
            id: 68,
            vendor: 'Nexus Health Solutions',
            gdc: '69050',
            gdcDescription: 'VITAMIN D3 1000 IU TABLET',
            gdcRank: '28',
            itemId: 'NHS00068',
            itemDescription: 'Vitamin D3 1000 IU tablets bottle 100',
            vendorsInAnchor: '12',
            bandVsMarket: '3.9% of market',
            bandVsLeader: '+6.8% above market',
            trend: 'stable'
        },
        {
            id: 69,
            vendor: 'Nexus Health Solutions',
            gdc: '74161',
            gdcDescription: 'CALCIUM CARBONATE 600 MG',
            gdcRank: '26',
            itemId: 'NHS00069',
            itemDescription: 'Calcium carbonate 600 mg tablets bottle 60',
            vendorsInAnchor: '14',
            bandVsMarket: '5.1% of market',
            bandVsLeader: '+4.9% above market',
            trend: 'up'
        },
        {
            id: 70,
            vendor: 'Infinity Drug Supply',
            gdc: '85272',
            gdcDescription: 'FISH OIL 1000 MG CAPSULE',
            gdcRank: '29',
            itemId: 'IDS00070',
            itemDescription: 'Fish oil 1000 mg capsules bottle 120',
            vendorsInAnchor: '10',
            bandVsMarket: '2.8% of market',
            bandVsLeader: '+7.9% above market',
            trend: 'up'
        },
        {
            id: 71,
            vendor: 'Infinity Drug Supply',
            gdc: '96383',
            gdcDescription: 'GLUCOSAMINE 1500 MG TABLET',
            gdcRank: '27',
            itemId: 'IDS00071',
            itemDescription: 'Glucosamine 1500 mg tablets bottle 60',
            vendorsInAnchor: '11',
            bandVsMarket: '3.6% of market',
            bandVsLeader: '+6.2% above market',
            trend: 'stable'
        },
        {
            id: 72,
            vendor: 'Infinity Drug Supply',
            gdc: '47494',
            gdcDescription: 'COENZYME Q10 100 MG CAPSULE',
            gdcRank: '30',
            itemId: 'IDS00072',
            itemDescription: 'Coenzyme Q10 100 mg capsules bottle 30',
            vendorsInAnchor: '7',
            bandVsMarket: '1.9% of market',
            bandVsLeader: '+9.8% above market',
            trend: 'up'
        },
        {
            id: 73,
            vendor: 'Cosmos Pharmaceutical',
            gdc: '58605',
            gdcDescription: 'PROBIOTICS 10 BILLION CFU',
            gdcRank: '28',
            itemId: 'COS00073',
            itemDescription: 'Probiotics 10 billion CFU capsules bottle 30',
            vendorsInAnchor: '9',
            bandVsMarket: '2.7% of market',
            bandVsLeader: '+8.1% above market',
            trend: 'stable'
        },
        {
            id: 74,
            vendor: 'Cosmos Pharmaceutical',
            gdc: '69716',
            gdcDescription: 'MAGNESIUM 400 MG TABLET',
            gdcRank: '25',
            itemId: 'COS00074',
            itemDescription: 'Magnesium 400 mg tablets bottle 100',
            vendorsInAnchor: '13',
            bandVsMarket: '4.5% of market',
            bandVsLeader: '+5.3% above market',
            trend: 'up'
        },
        {
            id: 75,
            vendor: 'Cosmos Pharmaceutical',
            gdc: '74827',
            gdcDescription: 'ZINC 50 MG TABLET',
            gdcRank: '29',
            itemId: 'COS00075',
            itemDescription: 'Zinc 50 mg tablets bottle 100',
            vendorsInAnchor: '8',
            bandVsMarket: '2.2% of market',
            bandVsLeader: '+9.1% above market',
            trend: 'up'
        }
    ];

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const filteredAndSortedData = React.useMemo(() => {
        // Since we're using server-side pagination and search, 
        // just return the current pricing data as-is
        let data = pricingData;
        
        // Apply client-side sorting if needed (server doesn't handle sorting yet)
        if (sortConfig.key) {
            data = [...data].sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return data;
    }, [pricingData, sortConfig]);

    // Pagination calculations (server-side pagination)
    const totalItems = totalCount;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
    const currentData = filteredAndSortedData; // Already paginated by server
    
    // Reset to first page when search term changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const getTrendIcon = (trend) => {
        switch (trend) {
            case 'up':
                return <span className="text-green-600">▲</span>;
            case 'down':
                return <span className="text-red-600">▼</span>;
            case 'stable':
                return <span className="text-gray-600">●</span>;
            default:
                return null;
        }
    };

    const getTrendColor = (trend) => {
        switch (trend) {
            case 'up':
                return 'text-green-600';
            case 'down':
                return 'text-red-600';
            case 'stable':
                return 'text-gray-600';
            default:
                return 'text-gray-600';
        }
    };

    if (isLoading) {
        return (
            <div className="bg-white rounded-lg shadow border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-200">
                    <h2 className="text-sm font-semibold text-gray-900">Pricing Intelligence Table</h2>
                </div>
                <div className="p-8 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-sm text-gray-500">Loading pricing intelligence data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-900">Pricing Intelligence Table</h2>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <input
                            type="text"
                            placeholder="Search descriptions..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            style={{
                                width: '16rem',
                                paddingLeft: '2.5rem',
                                paddingRight: searchInput ? '2.5rem' : '0.75rem',
                                paddingTop: '0.375rem',
                                paddingBottom: '0.375rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                backgroundColor: 'white',
                                outline: 'none',
                                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'/%3e%3c/svg%3e")`,
                                backgroundPosition: 'left 0.75rem center',
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: '1rem 1rem'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#3b82f6';
                                e.target.style.boxShadow = '0 0 0 1px #3b82f6';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#d1d5db';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    right: '0.75rem',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    color: '#9ca3af'
                                }}
                                onMouseEnter={(e) => e.target.style.color = '#6b7280'}
                                onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                            >
                                <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
                {searchTerm && (
                    <div className="mt-2 text-xs text-gray-600">
                        Found {totalItems} result{totalItems !== 1 ? 's' : ''} for "{searchTerm}"
                    </div>
                )}
            </div>
            
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                        <tr>
                            <th 
                                className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('vendor')}
                            >
                                Vendor Name
                                {sortConfig.key === 'vendor' && (
                                    <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                )}
                            </th>
                            <th 
                                className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('gdc')}
                            >
                                GDC
                            </th>
                            <th 
                                className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('gdcDescription')}
                            >
                                GDC Description
                            </th>
                            <th 
                                className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('gdcRank')}
                            >
                                GDC Rank
                            </th>
                            <th 
                                className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('itemId')}
                            >
                                Item ID
                            </th>
                            <th 
                                className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('itemDescription')}
                            >
                                Item Description
                            </th>
                            <th 
                                className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('vendorsInAnchor')}
                            >
                                Vendors
                            </th>
                            <th 
                                className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('bandVsMarket')}
                                title={filters.dateRange && filters.dateRange !== '1m' ? 
                                    `Trend over ${filters.dateRange} for Band vs Market comparison` : 
                                    "Comparison of your item price to the market average"}
                            >
                                Band vs. Market
                                {filters.dateRange && filters.dateRange !== '1m' && (
                                    <span className="ml-1 text-green-600">📈</span>
                                )}
                            </th>
                            <th 
                                className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('bandVsLeader')}
                                title={filters.dateRange && filters.dateRange !== '1m' ? 
                                    `Trend over ${filters.dateRange} for Band vs Leader comparison` : 
                                    "Comparison of your item price to the leading vendor"}
                            >
                                Band vs. Leader
                                {filters.dateRange && filters.dateRange !== '1m' && (
                                    <span className="ml-1 text-blue-600">📈</span>
                                )}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {currentData.map((item, index) => {
                            // Determine if we should show trend charts (when date range is not 1m)
                            const showTrendCharts = filters.dateRange && filters.dateRange !== '1m';
                            
                            // Debug logging (remove after testing)
                            if (index === 0) {
                                console.log('🔍 Debug - filters.dateRange:', filters.dateRange);
                                console.log('🔍 Debug - showTrendCharts:', showTrendCharts);
                            }
                            
                            // Generate trend data for charts
                            const marketTrendData = showTrendCharts ? 
                                generateTrendData(filters.dateRange, item.bandVsMarket, item.itemId) : null;
                            const leaderTrendData = showTrendCharts ? 
                                generateTrendData(filters.dateRange, item.bandVsLeader, item.itemId) : null;
                            
                            return (
                                <tr key={item.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${showTrendCharts ? 'h-14' : 'h-8'}`}>
                                    <td className="px-2 py-2 whitespace-nowrap">
                                        <span className="text-gray-900 font-medium text-xs">{item.vendor}</span>
                                    </td>
                                    <td className="px-2 py-2 whitespace-nowrap text-gray-900">{item.gdc}</td>
                                    <td className="px-2 py-2 text-gray-900 max-w-xs">
                                        <div className="truncate" title={item.gdcDescription}>
                                            {item.gdcDescription}
                                        </div>
                                    </td>
                                    <td className="px-2 py-2 whitespace-nowrap text-center text-gray-900">{item.gdcRank}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-gray-900 font-medium">{item.itemId}</td>
                                    <td className="px-2 py-2 text-gray-900 max-w-xs">
                                        <div className="truncate" title={item.itemDescription}>
                                            {item.itemDescription}
                                        </div>
                                    </td>
                                    <td className="px-2 py-2 whitespace-nowrap text-center text-gray-900">{item.vendorsInAnchor}</td>
                                    <td className="px-2 py-2 whitespace-nowrap">
                                        {showTrendCharts ? (
                                            <InlineTrendChart 
                                                data={marketTrendData}
                                                color="#10b981"
                                                currentValue={item.bandVsMarket}
                                            />
                                        ) : (
                                            <div className="flex items-center">
                                                {getTrendIcon(item.trend)}
                                                <span className="ml-1 text-gray-900">{item.bandVsMarket}</span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-2 py-2 whitespace-nowrap">
                                        {showTrendCharts ? (
                                            <InlineTrendChart 
                                                data={leaderTrendData}
                                                color="#3b82f6"
                                                currentValue={item.bandVsLeader}
                                            />
                                        ) : (
                                            <div className="flex items-center">
                                                {getTrendIcon(item.trend)}
                                                <span className={`ml-1 font-medium ${getTrendColor(item.trend)}`}>
                                                    {item.bandVsLeader}
                                                </span>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-gray-500">
                        <span>
                            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} records
                        </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            className="px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        
                        <div className="flex items-center space-x-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }
                                
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => handlePageChange(pageNum)}
                                        className={`px-2 py-1 text-xs font-medium rounded ${
                                            currentPage === pageNum
                                                ? 'text-blue-600 bg-blue-50 border border-blue-300'
                                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>
                        
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className="px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                    
                    <div className="text-xs text-gray-500">
                        Last updated: {new Date().toLocaleString()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PricingIntelligenceTable;
